import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { Container, LoadingOverlay, Alert, Button, Text } from '@mantine/core';
import { IconLock, IconArrowLeft } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRegistration } from '../../hooks/useRegistration';
import Layout from '../../components/Layout/Layout';
import ProgressStepper from '../../components/Registration/ProgressStepper';
import Step1Information from './Step1Information';
import Step2Consent from './Step2Consent';
import Step3Variations from './Step3Variations';
import Step4Payment from './Step4Payment';
import RegistrationClosed from './RegistrationClosed';
import { getActiveSeason, isRegistrationOpenForUser, usesCart, CONTACT_EMAIL } from '../../config/seasons';

/**
 * IndividualRegistration Container
 * Main container for the multi-step individual dancer registration flow
 */
const IndividualRegistration = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);

  // Prevent duplicate initialization
  const hasInitializedRef = useRef(false);

  // Season gate — decided once per render from the config. Includes per-user
  // late-access overrides so an approved dancer can finish after close.
  const activeSeason = getActiveSeason();
  const seasonOpen = isRegistrationOpenForUser(activeSeason.year, user?.email);

  // Get or create registration
  const {
    registration,
    dancerInfo,
    consents,
    variations,
    loading,
    error,
    saving,
    createRegistration,
    loadRegistration,
    saveDancerInfo,
    saveConsents,
    saveVariation,
    updateRegistrationData
  } = useRegistration();

  /**
   * When the season is closed but this user is paid + complete + music-uploaded,
   * we let them into the step routes in read-only mode instead of redirecting
   * to /profile. Computed here (not in a useEffect) so downstream hooks that
   * depend on it can reference a stable value.
   */
  const closedReadOnly = (() => {
    if (seasonOpen) return false;
    if (!user || !registration) return false;
    if (registration.status !== 'complete' && registration.payment_status !== 'paid') return false;
    // Every solo needs its music. 2026 required at least one solo; from 2027 a
    // dancer may be in ensembles only (those entries live behind /api/ensemble,
    // not in `variations`), so having no solos is fine too.
    const solos = Array.isArray(variations) ? variations : [];
    const solosHaveMusic = solos.every(v => v.music_file_url);
    const hasEntries = solos.length > 0 || usesCart(registration.competition_year);
    return solosHaveMusic && hasEntries;
  })();

  /**
   * Initialize registration on mount - ONE registration per user
   * Uses ref to prevent duplicate initialization in React StrictMode
   */
  useEffect(() => {
    const initializeRegistration = async () => {
      if (!user) {
        console.log('❌ No user, redirecting to login');
        // Redirect to login with current path as redirect parameter
        navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`); // keeps ?team= invites
        return;
      }

      // Prevent duplicate initialization (important for React StrictMode)
      if (hasInitializedRef.current) {
        console.log('⚠️ Already initialized, skipping');
        return;
      }

      // Only run once when not loading
      if (loading) {
        return;
      }

      // Mark as initializing
      hasInitializedRef.current = true;

      try {
        // Check if user already has a registration this season (ONE per user per season)
        if (!registration) {
          console.log('📋 Loading user registration...');
          const { getUserRegistrations } = await import('../../services/registrationService');
          const existingRegs = await getUserRegistrations(user.id);

          console.log(`Found ${existingRegs?.length || 0} existing registrations`);

          // Only this season's individual registration counts (user may also have
          // school registrations). Without the year filter, a dancer paid for an
          // earlier season was sent to /success instead of starting a new one.
          const individualRegs = (existingRegs || []).filter(r =>
            r.registration_type === 'individual' && r.competition_year === activeSeason.year
          );

          if (individualRegs.length > 0) {
            // Prioritize paid/submitted registrations over drafts
            const paidReg = individualRegs.find(r =>
              r.payment_status === 'paid' || r.status === 'paid' || r.status === 'complete' || r.status === 'submitted'
            );
            const userRegistration = paidReg || individualRegs[0];
            console.log('✅ Found existing registration:', userRegistration.id);
            console.log('Status:', userRegistration.status, 'Step:', userRegistration.current_step);

            // Load this registration first
            await loadRegistration(userRegistration.id);

            // Note: If it's complete and they want to edit, we'll handle that in the form steps
            // No need to automatically reset it here
          } else if (seasonOpen) {
            console.log('📝 No registration found, creating first registration for user:', user.id);
            const newReg = await createRegistration();
            if (newReg) {
              console.log('✅ Registration created:', newReg.id);
            } else {
              console.error('❌ Failed to create registration');
              hasInitializedRef.current = false; // Reset on failure so user can retry
            }
          } else {
            console.log('🔒 Season closed and no existing registration — skipping auto-create.');
          }
        } else {
          console.log('✅ Registration already loaded:', registration.id);
        }
      } catch (err) {
        console.error('❌ Error initializing registration:', err);
        hasInitializedRef.current = false; // Reset on error so user can retry
      }
    };

    if (!authLoading) {
      initializeRegistration();
    }
  }, [user, authLoading, loading, registration, createRegistration, loadRegistration, navigate, seasonOpen, activeSeason.year]);

  /**
   * Determine current step from URL. Skipped entirely when the season is
   * closed AND the user isn't in the read-only pathway — the closed gate
   * below handles that render.
   *
   * When we're in read-only mode we still want URL → step tracking (so the
   * progress stepper highlights the right step), but we do NOT auto-navigate
   * to /success — the user is here to view, not finish.
   */
  useEffect(() => {
    if (!seasonOpen && !closedReadOnly) return;
    const path = location.pathname;
    if (path.includes('step-1')) setCurrentStep(1);
    else if (path.includes('step-2')) setCurrentStep(2);
    else if (path.includes('step-3')) setCurrentStep(3);
    else if (path.includes('step-4')) setCurrentStep(4);
    else if (closedReadOnly) {
      // Enter view-only mode at step-1 by default.
      navigate('/register/individual/step-1', { replace: true });
    } else {
      // Open-season flow: paid users → their entries (2027+, to add more) or
      // success (2026 flat fee); others → resume from saved step.
      if (registration?.payment_status === 'paid' || registration?.status === 'complete' || registration?.status === 'paid' || registration?.status === 'submitted') {
        navigate(usesCart(registration.competition_year) ? '/register/individual/step-3' : '/register/individual/success', { replace: true });
        return;
      }
      const resumeStep = registration?.current_step || 1;
      navigate(`/register/individual/step-${resumeStep}`, { replace: true });
    }
  }, [location.pathname, registration, navigate, seasonOpen, closedReadOnly]);

  /**
   * 2026 (flat fee): a paid registration skips the payment step. 2027+ always
   * goes to Step 4, which asks the server what's unpaid and shows "all paid
   * up" when nothing is (the browser's copy can be stale right after a save).
   */
  const isAlreadyPaid = registration && !usesCart(registration.competition_year) &&
    (registration.status === 'complete' || registration.payment_status === 'paid');

  /**
   * Navigation handlers. In read-only mode we never bounce to /success —
   * that page shows "Information Saved!" and would be misleading since
   * nothing was actually saved.
   */
  const goToStep = (step) => {
    if (step === 4 && isAlreadyPaid && !closedReadOnly) {
      console.log('⚠️ Skipping payment step - registration already paid');
      navigate('/register/individual/success?edit=true');
      return;
    }
    navigate(`/register/individual/step-${step}`);
  };

  const goToNextStep = () => {
    const nextStep = currentStep + 1;

    if (nextStep === 4 && isAlreadyPaid && !closedReadOnly) {
      console.log('⚠️ Skipping payment step - registration already paid');
      navigate('/register/individual/success?edit=true');
      return;
    }

    if (nextStep <= 4) {
      goToStep(nextStep);
    } else if (!closedReadOnly) {
      navigate('/register/individual/success');
    }
  };

  const goToPreviousStep = () => {
    const prevStep = currentStep - 1;
    if (prevStep >= 1) {
      goToStep(prevStep);
    } else {
      navigate('/register');
    }
  };

  /**
   * Show loading state
   */
  if (authLoading || loading) {
    return (
      <Layout title="Registration - Prix du Canada">
        <Container size="lg" style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay visible={true} />
        </Container>
      </Layout>
    );
  }

  /**
   * Season-closed gate. Paid+complete+music users fall through to the step
   * routes rendered read-only (see `closedReadOnly` at top). Everyone else
   * gets the RegistrationClosed page tailored to their state.
   */
  if (!seasonOpen && !closedReadOnly) {
    if (!user)          return <RegistrationClosed variant="no_reg" year={activeSeason.year} />;
    if (!registration)  return <RegistrationClosed variant="no_reg" year={activeSeason.year} />;
    if (registration.status !== 'complete' && registration.payment_status !== 'paid') {
      return <RegistrationClosed variant="draft" year={activeSeason.year} />;
    }
    // Paid but no music — hard close, manual help needed.
    return <RegistrationClosed variant="paid_no_music" year={activeSeason.year} />;
  }

  /**
   * Show error state
   */
  if (error) {
    return (
      <Layout title="Registration - Prix du Canada">
        <Container size="lg" style={{ padding: '3rem 1rem' }}>
          <Alert color="red" title="Error">
            {error}
            <br /><br />
            <small>Debug info: User ID: {user?.id || 'No user'}</small>
            <br />
            <small>Registration: {registration?.id || 'None'}</small>
            <br />
            <small>Loading: {loading ? 'Yes' : 'No'}</small>
          </Alert>
        </Container>
      </Layout>
    );
  }

  /**
   * Wait for registration to be created
   */
  if (!registration && !error) {
    return (
      <Layout title="Registration - Prix du Canada">
        <Container size="lg" style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay visible={true} />
        </Container>
      </Layout>
    );
  }

  /**
   * Require authentication
   */
  if (!user) {
    return null; // Will redirect to login in useEffect
  }

  /**
   * Shared props for all steps
   */
  const stepProps = {
    registration,
    dancerInfo,
    consents,
    variations,
    saving,
    saveDancerInfo,
    saveConsents,
    saveVariation,
    updateRegistrationData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    isComplete: isAlreadyPaid, // Flag to indicate if editing completed registration
    readOnly: closedReadOnly,  // Season closed + paid+complete+music → view-only
    refreshRegistration: () => registration?.id && loadRegistration(registration.id)
  };

  return (
    <Layout
      title="Individual Registration - Prix du Canada"
        description={`Complete your individual dancer registration for Prix du Canada ${activeSeason.year}`}
      >
        <ProgressStepper currentStep={currentStep} isComplete={isAlreadyPaid} />

        <Container size="lg" px={{ base: 4, sm: 'md' }} style={{ paddingTop: 'clamp(0.5rem, 2vw, 2rem)', paddingBottom: '4rem' }}>
          {closedReadOnly && (
            <Alert
              icon={<IconLock size={18} />}
              color="yellow"
              variant="light"
              mb="md"
              title="Viewing your submitted registration"
            >
              <Text size="sm" mb="sm">
                Registration for {activeSeason.year} is closed. You can browse your submitted information across all steps below, but nothing can be edited. Need a change? Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>
                  {CONTACT_EMAIL}
                </a>.
              </Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconArrowLeft size={14} />}
                onClick={() => navigate('/profile')}
              >
                Back to profile
              </Button>
            </Alert>
          )}
          <Routes>
            <Route path="step-1" element={<Step1Information {...stepProps} />} />
            <Route path="step-2" element={<Step2Consent {...stepProps} />} />
            <Route path="step-3" element={<Step3Variations {...stepProps} />} />
            <Route path="step-4" element={<Step4Payment {...stepProps} />} />
          </Routes>
        </Container>
      </Layout>
  );
};

export default IndividualRegistration;
